/** Merge these feature keys into each locale's translation object at integration. */
export const aiProviderCopy = {
  en: {
    title: 'AI providers & voice',
    description: 'Connect Gemini for AI authoring and narration. Credentials stay on the server.',
    connection: 'Gemini connection',
    status: {
      disconnected: 'Not connected',
      saved: 'Saved, not verified',
      verified: 'Verified',
      disabled: 'Disabled',
      error: 'Needs attention',
    },
    keyHelp:
      'The saved API key is never displayed. Enter a new key only when connecting or rotating it.',
    apiKey: 'Gemini API key',
    replaceKey: 'Replace API key',
    keyPlaceholder: 'Enter API key',
    maskedKey: 'Saved key: {{hint}}',
    textModel: 'Text model',
    ttsModel: 'Narration model',
    dailyNarrationLimit: 'Daily narration character limit',
    save: 'Save configuration',
    test: 'Check model access',
    disable: 'Disable provider',
    loadError: 'Could not load provider settings',
    retry: 'Retry',
    testHelp: {
      notConnected: 'Save an API key to connect Gemini before checking model access.',
      unsaved:
        'Check model access uses the saved key and models, not what is typed above. Save your changes first.',
    },
    verification: {
      title: 'Model access check',
      checkedAt: 'Checked {{time}}',
      never: 'Not checked yet. Saving does not verify the key: save, then check model access.',
      scope:
        'Both models were found with this key. Only model access is checked, so no text or audio is generated and nothing is billed.',
      role: {
        text: 'Text model',
        tts: 'Narration model',
      },
    },
    categories: {
      ok: { label: 'Available' },
      invalid_key: {
        label: 'Key rejected',
        hint: 'Google rejected the API key. It may be mistyped, expired or revoked. Enter a valid key and save it.',
      },
      request_rejected: {
        label: 'Request rejected',
        hint: 'Google rejected the request. The API key or a model ID may be wrong or expired. Enter the key again and save it, and check that both model IDs are correct.',
      },
      permission_denied: {
        label: 'Access denied',
        hint: 'The key is not allowed to use these models. Check the key restrictions and that the Generative Language API is enabled for its Google project.',
      },
      model_unavailable: {
        label: 'Model not found',
        hint: 'Google does not offer a model ID to this key. Pick a model ID that your Google account can use, then save.',
      },
      rate_limited: {
        label: 'Rate limited',
        hint: 'Google is rate limiting this key or its quota is used up. Wait a few minutes and check again, or review the quota in Google AI Studio.',
      },
      timeout: {
        label: 'Timed out',
        hint: 'Google did not answer in time. Check again in a moment.',
      },
      network_error: {
        label: 'Not reachable',
        hint: 'The server could not reach Google. Check the server network or firewall, then try again.',
      },
      provider_error: {
        label: 'Provider error',
        hint: 'Google returned an unexpected error. Try again shortly; if it keeps happening, check Google AI status.',
      },
      key_unreadable: {
        label: 'Key unreadable',
        hint: 'The saved key cannot be read any more. Enter the API key again and save it.',
      },
    },
    feedback: {
      saved: 'Settings saved. They are not verified yet: run Check model access.',
      connected: 'Model access confirmed. Audio generation was not tested.',
      disabled: 'Provider disabled and the saved key removed.',
    },
    errors: {
      default: 'The provider action failed. Try again.',
      UNKNOWN: 'The provider action failed. Check your connection and try again.',
      COMPANY_CONFLICT: 'Settings changed elsewhere. Review the latest values and retry.',
      ACCESS_DENIED: 'You do not have permission to manage this provider.',
      AI_PROVIDER_NOT_CONFIGURED: 'Save an API key before checking model access.',
      VALIDATION_FAILED: 'Some values are not valid. Check the fields and try again.',
    },
  },
  fa: {
    title: 'ارائه‌دهندگان هوش مصنوعی و صدا',
    description: 'جمینای را برای نگارش و روایت متصل کنید. اطلاعات محرمانه در سرور می‌ماند.',
    connection: 'اتصال جمینای',
    status: {
      disconnected: 'متصل نیست',
      saved: 'ذخیره‌شده، تأییدنشده',
      verified: 'تأییدشده',
      disabled: 'غیرفعال',
      error: 'نیازمند بررسی',
    },
    keyHelp: 'کلید ذخیره‌شده نمایش داده نمی‌شود. برای اتصال یا تعویض، کلید جدید وارد کنید.',
    apiKey: 'کلید API جمینای',
    replaceKey: 'تعویض کلید API',
    keyPlaceholder: 'کلید API را وارد کنید',
    maskedKey: 'کلید ذخیره‌شده: {{hint}}',
    textModel: 'مدل متن',
    ttsModel: 'مدل روایت',
    dailyNarrationLimit: 'سقف روزانه نویسه‌های روایت',
    save: 'ذخیره تنظیمات',
    test: 'بررسی دسترسی به مدل',
    disable: 'غیرفعال‌کردن ارائه‌دهنده',
    loadError: 'تنظیمات ارائه‌دهنده بارگذاری نشد',
    retry: 'تلاش دوباره',
    testHelp: {
      notConnected: 'برای بررسی دسترسی، ابتدا کلید API را ذخیره و جمینای را متصل کنید.',
      unsaved:
        'بررسی دسترسی از کلید و مدل‌های ذخیره‌شده استفاده می‌کند، نه مقادیر واردشده در فرم. ابتدا تغییرات را ذخیره کنید.',
    },
    verification: {
      title: 'بررسی دسترسی به مدل',
      checkedAt: 'زمان بررسی: {{time}}',
      never:
        'هنوز بررسی نشده است. ذخیره‌کردن کلید را تأیید نمی‌کند؛ ابتدا ذخیره کنید، سپس دسترسی را بررسی کنید.',
      scope:
        'هر دو مدل با این کلید پیدا شدند. فقط دسترسی به مدل بررسی می‌شود؛ متن یا صدایی تولید نمی‌شود و هزینه‌ای ندارد.',
      role: {
        text: 'مدل متن',
        tts: 'مدل روایت',
      },
    },
    categories: {
      ok: { label: 'در دسترس' },
      invalid_key: {
        label: 'کلید رد شد',
        hint: 'گوگل کلید API را نپذیرفت. ممکن است اشتباه تایپ شده، منقضی یا لغو شده باشد. کلید معتبر را وارد و ذخیره کنید.',
      },
      request_rejected: {
        label: 'درخواست رد شد',
        hint: 'گوگل درخواست را نپذیرفت. ممکن است کلید API یا شناسهٔ یکی از مدل‌ها اشتباه یا منقضی باشد. کلید را دوباره وارد و ذخیره کنید و درستی شناسهٔ هر دو مدل را بررسی کنید.',
      },
      permission_denied: {
        label: 'دسترسی ندارد',
        hint: 'کلید اجازهٔ استفاده از این مدل‌ها را ندارد. محدودیت‌های کلید را بررسی کنید و مطمئن شوید Generative Language API برای پروژهٔ گوگل آن فعال است.',
      },
      model_unavailable: {
        label: 'مدل پیدا نشد',
        hint: 'گوگل این شناسهٔ مدل را برای این کلید ارائه نمی‌کند. شناسهٔ مدلی را انتخاب کنید که حساب گوگل شما به آن دسترسی دارد و ذخیره کنید.',
      },
      rate_limited: {
        label: 'محدودیت نرخ',
        hint: 'گوگل درخواست‌های این کلید را محدود کرده یا سهمیه تمام شده است. چند دقیقه صبر کنید و دوباره بررسی کنید، یا سهمیه را در Google AI Studio ببینید.',
      },
      timeout: {
        label: 'پایان زمان',
        hint: 'گوگل به‌موقع پاسخ نداد. کمی بعد دوباره بررسی کنید.',
      },
      network_error: {
        label: 'در دسترس نیست',
        hint: 'سرور نتوانست به گوگل برسد. شبکه یا فایروال سرور را بررسی کنید و دوباره تلاش کنید.',
      },
      provider_error: {
        label: 'خطای ارائه‌دهنده',
        hint: 'گوگل خطای غیرمنتظره‌ای برگرداند. کمی بعد دوباره تلاش کنید؛ اگر ادامه داشت وضعیت سرویس‌های گوگل را ببینید.',
      },
      key_unreadable: {
        label: 'کلید خوانده نشد',
        hint: 'کلید ذخیره‌شده دیگر قابل خواندن نیست. کلید API را دوباره وارد و ذخیره کنید.',
      },
    },
    feedback: {
      saved: 'تنظیمات ذخیره شد. هنوز تأیید نشده است؛ دسترسی به مدل را بررسی کنید.',
      connected: 'دسترسی به مدل تأیید شد. تولید صدا آزمایش نشد.',
      disabled: 'ارائه‌دهنده غیرفعال و کلید ذخیره‌شده حذف شد.',
    },
    errors: {
      default: 'عملیات ناموفق بود. دوباره تلاش کنید.',
      UNKNOWN: 'عملیات ناموفق بود. اتصال خود را بررسی کنید و دوباره تلاش کنید.',
      COMPANY_CONFLICT: 'تنظیمات تغییر کرده است. مقادیر جدید را بررسی کنید.',
      ACCESS_DENIED: 'مجوز مدیریت این ارائه‌دهنده را ندارید.',
      AI_PROVIDER_NOT_CONFIGURED: 'پیش از بررسی دسترسی، کلید API را ذخیره کنید.',
      VALIDATION_FAILED: 'برخی مقادیر معتبر نیستند. فیلدها را بررسی کنید و دوباره تلاش کنید.',
    },
  },
} as const;
